/**
 * A Plaud share link, and what can be got out of one.
 *
 * ADR 0064 assumed recordings would arrive by themselves. They cannot yet —
 * Plaud's API for reading your own recordings is a private beta behind a
 * waitlist — so the first working way in is the one a rep can do today with no
 * permission from anybody: open the recording in Plaud, press Share, copy the
 * link, paste it onto the job. ADR 0065 records the decision.
 *
 * Two things a rep can paste, and both are handled:
 *
 *   1. **A share link.** Roof Board fetches the page and reads the summary and
 *      the transcript off it.
 *   2. **The words themselves.** If the link will not open — it was made
 *      invite-only, or shared without the transcript ticked — pasting the text
 *      out of Plaud still works. It is more effort per recording and it always
 *      works, which is the right shape for a fallback.
 *
 * Everything here is pure. The fetching is in the `plaud-import` Edge Function;
 * deciding what a paste *is*, and what can be believed about a fetched page, is
 * business logic and lives here (CLAUDE.md rule 5).
 */

/**
 * The hosts a Plaud share link lives on.
 *
 * Deliberately a list rather than "anything ending plaud.ai": the Edge Function
 * fetches whatever this approves, so a loose rule here is a rep pasting any URL
 * and Roof Board's server dutifully requesting it. Add a host when Plaud starts
 * using one, and not before.
 */
export const PLAUD_SHARE_HOSTS = [
  "plaud.ai",
  "www.plaud.ai",
  "web.plaud.ai",
  "app.plaud.ai",
  "share.plaud.ai",
] as const;

/** What a paste turned out to be. */
export type PasteKind = "link" | "words" | "nothing";

/** Enough words to be a conversation rather than a slip of the mouse. */
export const PASTED_MINIMUM = 200;

/**
 * Is this a link, some words, or neither?
 *
 * The rep gets one box, not two, because "is this a URL" is a question a
 * computer can answer and a question nobody should be asked.
 */
export function pasteKind(input: string): PasteKind {
  const trimmed = input.trim();
  if (trimmed === "") return "nothing";
  if (plaudShareUrl(trimmed) !== null) return "link";
  if (/^https?:\/\//i.test(trimmed) && !trimmed.includes(" ")) return "link";
  return trimmed.length >= PASTED_MINIMUM ? "words" : "nothing";
}

/**
 * The share link, normalised, or null if it is not one.
 *
 * Normalising matters because the same recording pasted twice must look the
 * same twice — the unique index on `plaud_share_url` is what stops one
 * conversation becoming two, and it compares strings. So: lower-case host,
 * no query, no fragment, no trailing slash. The query goes because Plaud's
 * own copy button has been known to append tracking, and a link with `?from=web`
 * is not a different recording.
 */
export function plaudShareUrl(input: string): string | null {
  let url: URL;
  try {
    url = new URL(input.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const host = url.hostname.toLowerCase();
  if (!(PLAUD_SHARE_HOSTS as readonly string[]).includes(host)) return null;

  const path = url.pathname.replace(/\/+$/, "");
  if (path === "") return null;

  return `https://${host}${path}`;
}

/** Why this paste cannot be imported, in words a rep can act on. */
export function pasteBlocker(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === "")
    return "Paste a Plaud share link, or the transcript itself.";

  if (/^https?:\/\//i.test(trimmed) && !trimmed.includes(" ")) {
    return plaudShareUrl(trimmed) === null
      ? "That is a link, but not a Plaud one. Open the recording in Plaud, press Share, and copy the link from there."
      : null;
  }

  if (trimmed.length < PASTED_MINIMUM) {
    return "That is too short to be a conversation. Paste the Plaud share link, or the whole transcript.";
  }
  return null;
}

/* ------------------------------------------- reading a fetched page --- */

/** What we managed to read off a share page. */
export interface SharedRecording {
  name: string | null;
  startedAt: string | null;
  durationMs: number | null;
  summaryMd: string | null;
  transcriptText: string | null;
  segments: TranscriptSegment[] | null;
}

/** One line of a speaker-labelled transcript. */
export interface TranscriptSegment {
  start: number | null;
  end: number | null;
  speaker: string | null;
  text: string;
}

/**
 * A transcript flattened to the "Speaker: text" the prompts and the search want.
 *
 * Written once at import rather than rebuilt on every read, and it is the only
 * form anything downstream is allowed to depend on — a share page and a poller
 * will not label speakers the same way, and everything after this point should
 * not have to know which one it came from.
 */
export function transcriptText(segments: TranscriptSegment[]): string {
  return segments
    .map((segment) => {
      const said = segment.text.trim();
      if (said === "") return "";
      return segment.speaker ? `${segment.speaker}: ${said}` : said;
    })
    .filter((line) => line !== "")
    .join("\n");
}

/**
 * Read a recording out of whatever JSON a share page carries.
 *
 * **This is a guess until a real share link has been seen.** Plaud's support
 * pages would not load from the machine this was written on, so the shapes
 * below are the ones such pages usually take, and the function is deliberately
 * built to return *partial* answers rather than throw: a page that yields a
 * transcript and no summary is still worth importing, and one that yields
 * nothing says so plainly instead of pretending.
 *
 * When the first real link disagrees with this, change this function and the
 * fixtures beside it. Nothing else has to move.
 */
export function readSharedRecording(payload: unknown): SharedRecording | null {
  const record = deepFind(payload, (value) => {
    if (!value || typeof value !== "object") return false;
    const candidate = value as Record<string, unknown>;
    return (
      "transcript" in candidate ||
      "source_list" in candidate ||
      "sourceList" in candidate ||
      "segments" in candidate ||
      "note_list" in candidate ||
      "summary" in candidate
    );
  });
  if (!record) return null;

  const bag = record as Record<string, unknown>;
  const segments = readSegments(
    bag.source_list ?? bag.sourceList ?? bag.segments ?? bag.transcript,
  );
  const summary = readSummary(bag.note_list ?? bag.noteList ?? bag.summary);
  const flattened = segments
    ? transcriptText(segments)
    : readText(bag.transcript);

  if (!summary && !flattened) return null;

  return {
    name: readText(bag.name ?? bag.title ?? bag.file_name) ?? null,
    startedAt: readTime(bag.start_at ?? bag.startAt ?? bag.created_at),
    durationMs: readDuration(bag.duration ?? bag.duration_ms ?? bag.durationMs),
    summaryMd: summary,
    transcriptText: flattened,
    segments,
  };
}

function readSegments(value: unknown): TranscriptSegment[] | null {
  if (!Array.isArray(value)) return null;
  const segments = value
    .map((entry) => {
      if (typeof entry === "string") {
        return { start: null, end: null, speaker: null, text: entry };
      }
      if (!entry || typeof entry !== "object") return null;
      const bag = entry as Record<string, unknown>;
      const said = readText(bag.text ?? bag.content ?? bag.sentence);
      if (!said) return null;
      return {
        start: readNumber(bag.start ?? bag.start_time ?? bag.startTime),
        end: readNumber(bag.end ?? bag.end_time ?? bag.endTime),
        speaker: readText(bag.speaker ?? bag.speaker_name ?? bag.role) ?? null,
        text: said,
      };
    })
    .filter((segment): segment is TranscriptSegment => segment !== null);
  return segments.length > 0 ? segments : null;
}

/**
 * The summary, as markdown.
 *
 * Plaud's own note is a list of blocks with headings; a share page may hand
 * over the rendered markdown instead. Both end up as one string, because the
 * only thing that reads it is a person and a prompt.
 */
function readSummary(value: unknown): string | null {
  const direct = readText(value);
  if (direct) return direct;
  if (!Array.isArray(value)) return null;

  const parts = value
    .map((entry) => {
      if (typeof entry === "string") return entry;
      if (!entry || typeof entry !== "object") return null;
      const bag = entry as Record<string, unknown>;
      const heading = readText(bag.title ?? bag.heading ?? bag.name);
      const body = readText(
        bag.content ?? bag.text ?? bag.markdown ?? bag.body,
      );
      if (!heading && !body) return null;
      return heading && body
        ? `## ${heading}\n\n${body}`
        : (body ?? `## ${heading}`);
    })
    .filter((part): part is string => part !== null && part.trim() !== "");

  return parts.length > 0 ? parts.join("\n\n") : null;
}

function readText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function readNumber(value: unknown): number | null {
  const number = typeof value === "string" ? Number(value) : value;
  return typeof number === "number" && Number.isFinite(number) ? number : null;
}

/**
 * How long it ran, in milliseconds.
 *
 * The same seconds-or-milliseconds trap as a photograph's timestamp, in the
 * other direction: read milliseconds as seconds and a half-hour conversation
 * becomes eighteen hundred hours. Anything under four hours expressed in
 * milliseconds is treated as seconds, because no sales call runs four hours and
 * plenty run more than 14,400 milliseconds.
 */
function readDuration(value: unknown): number | null {
  const number = readNumber(value);
  if (number === null || number <= 0) return null;
  return number < 4 * 60 * 60 ? Math.round(number * 1000) : Math.round(number);
}

function readTime(value: unknown): string | null {
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
  }
  const number = readNumber(value);
  if (number === null) return null;
  const millis = number < 1_000_000_000_000 ? number * 1000 : number;
  return new Date(millis).toISOString();
}

/**
 * Hunt through an arbitrarily nested blob for the first thing that looks right.
 *
 * A share page's embedded state is somebody else's shape, wrapped in however
 * many layers of `props.pageProps.data` they felt like. Walking it is uglier
 * than naming the path and survives them renaming a wrapper, which is the
 * likelier event.
 */
function deepFind(
  value: unknown,
  matches: (candidate: unknown) => boolean,
  depth = 0,
): unknown {
  if (depth > 8 || !value || typeof value !== "object") return null;
  if (matches(value)) return value;

  for (const child of Array.isArray(value) ? value : Object.values(value)) {
    const found = deepFind(child, matches, depth + 1);
    if (found) return found;
  }
  return null;
}

/* ------------------------------------------- reading the page HTML --- */

/**
 * The JSON blobs a share page carries, most promising first.
 *
 * A page like this is a JavaScript app: the words a person sees are not in the
 * HTML, but the data the app rendered them from usually is, in a `<script>` the
 * framework wrote. `__NEXT_DATA__` is the common one; `<script
 * type="application/json">` covers the rest.
 *
 * Returning every candidate rather than picking one keeps the guessing in one
 * place — `readSharedRecording` is what decides which of them is a recording,
 * and it can be handed all of them cheaply.
 */
export function sharePayloadsFromHtml(html: string): unknown[] {
  const payloads: unknown[] = [];
  const script =
    /<script\b[^>]*?(?:type=["'](?:application\/json|application\/ld\+json)["']|id=["']__NEXT_DATA__["'])[^>]*>([\s\S]*?)<\/script>/gi;

  for (const match of html.matchAll(script)) {
    const body = (match[1] ?? "").trim();
    if (body === "") continue;
    try {
      payloads.push(JSON.parse(body));
    } catch {
      // A script that is not the JSON it claimed to be is somebody else's
      // problem, not a reason to fail the import.
    }
  }
  return payloads;
}

/**
 * The recording on a share page, from its HTML.
 *
 * Null means the page had nothing we could use, which has three likely causes
 * and the screen says all three: the link is invite-only, the share was made
 * without the transcript ticked, or Plaud changed the page. In every one of
 * them, pasting the words still works.
 */
export function readSharePage(html: string): SharedRecording | null {
  for (const payload of sharePayloadsFromHtml(html)) {
    const read = readSharedRecording(payload);
    if (read) return read;
  }
  return null;
}

/* ------------------------------------------------ pasting the words --- */

/**
 * A pasted transcript, split into speakers where it says so.
 *
 * Plaud writes "Speaker 1: ..." at the start of a line, and a rep copying out
 * of Plaud brings that with them. The trap is prose: "We talked about the
 * following: gutters and decking" is not somebody called "We talked about the
 * following", and a naive split on the first colon says it is.
 *
 * So a label has to earn it. Either it is literally "Speaker 1", or the same
 * short prefix turns up on more than one line — which is what a real
 * transcript looks like and what a paragraph of prose never does. Anything
 * else stays unattributed, because an unlabelled line is honest and a
 * misattributed one is a quote put in somebody's mouth.
 */
const LABELLED_LINE = /^([A-Za-z][\w .'-]{0,31}?)\s*:\s+(.*)$/;
const NUMBERED_SPEAKER = /^speaker\s*\d+$/i;

function couldBeLabel(candidate: string): boolean {
  return candidate.trim().split(/\s+/).length <= 3;
}

export function segmentsFromPastedText(text: string): TranscriptSegment[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "");

  const seen = new Map<string, number>();
  for (const line of lines) {
    const match = LABELLED_LINE.exec(line);
    if (match && couldBeLabel(match[1]!)) {
      seen.set(match[1]!, (seen.get(match[1]!) ?? 0) + 1);
    }
  }

  const isSpeaker = (candidate: string) =>
    NUMBERED_SPEAKER.test(candidate) || (seen.get(candidate) ?? 0) > 1;

  return lines.map((line) => {
    const match = LABELLED_LINE.exec(line);
    return match && couldBeLabel(match[1]!) && isSpeaker(match[1]!)
      ? { start: null, end: null, speaker: match[1]!, text: match[2]! }
      : { start: null, end: null, speaker: null, text: line };
  });
}
